import type { NpiSuggestions } from './types';

/**
 * Where the picker is. `confirm` asks about one candidate, `list` offers
 * several, `name` asks for the person's name, `search` adds a state to it, and
 * `exhausted` leaves only typing the NPI.
 */
export type PickerStep = 'loading' | 'confirm' | 'list' | 'name' | 'search' | 'exhausted';

/** The first step for a set of suggestions. */
export function stepFor(suggestions: NpiSuggestions): PickerStep {
  if (suggestions.confidence === 'single') {
    return 'confirm';
  }

  if (suggestions.candidates.length > 0) {
    return 'list';
  }

  return 'name';
}

/**
 * After "Not me" on the single candidate: the others the server found, if
 * any, before asking for a name.
 */
export function stepAfterRejectingSingle(suggestions: NpiSuggestions): PickerStep {
  return suggestions.candidates.length > 1 ? 'list' : 'name';
}

/**
 * After a name search comes back. A search by name alone that finds no one
 * offers a state to narrow it; one that already had a state has nowhere left
 * to go.
 */
export function stepAfterNameSearch(
  suggestions: NpiSuggestions,
  searchedWithState: boolean
): PickerStep {
  if (suggestions.candidates.length > 0) {
    return stepFor(suggestions);
  }

  return searchedWithState ? 'exhausted' : 'search';
}
